/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box, Switch, FormControlLabel } from "@mui/material"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { WitnessInput } from "./components/WitnessInput"
import { saveImage, getImage, ImageKey } from "../../../../services/indexedDBService"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5vh;
    text-align: center;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const FormContainerStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 50vw;
    
    ${mobileCss(`
        gap: 1.5rem;
        width: 80vw;
    `)}
`)

const ToggleContainerStyle = withMyTheme(() => css`
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
`)

const ToggleLabelStyle = withMyTheme((theme) => css`
    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 1.1rem;
        font-weight: 500;
        color: ${theme.palette.text.primary};
    }
`)

const SwitchStyle = withMyTheme((theme) => css`
    & .MuiSwitch-switchBase.Mui-checked {
        color: ${theme.palette.primary.main};
    }
    & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: ${theme.palette.primary.main};
    }
`)

interface WitnessesStepProps {
    onStart?: () => void
}

export const WitnessesStep = ({}: WitnessesStepProps) => {
    const { t } = useTranslation()
    const { formData, updateWitnesses, storageContext, markImageChanged } = useQuestionnaireForm()
    const [witness1ImageData, setWitness1ImageData] = useState<string>('')
    const [witness2ImageData, setWitness2ImageData] = useState<string>('')
    const [loading, setLoading] = useState(true)

    // Load images from IndexedDB on mount
    useEffect(() => {
        const loadImages = async () => {
            if (formData.witnesses.witness1.image) {
                const data = await getImage(ImageKey.WITNESS1_IMAGE, storageContext)
                if (data) {
                    setWitness1ImageData(data)
                }
            }
            if (formData.witnesses.witness2.image) {
                const data = await getImage(ImageKey.WITNESS2_IMAGE, storageContext)
                if (data) {
                    setWitness2ImageData(data)
                }
            }
            setLoading(false)
        }
        loadImages()
    }, [formData.witnesses.witness1.image, formData.witnesses.witness2.image, storageContext])

    const handleWitness1NameChange = (name: string) => {
        updateWitnesses({
            witness1: {
                ...formData.witnesses.witness1,
                name
            }
        })
    }

    const handleWitness1ImageChange = async (image: string) => {
        try {
            await saveImage(ImageKey.WITNESS1_IMAGE, image, storageContext)
            setWitness1ImageData(image)
            updateWitnesses({
                witness1: {
                    ...formData.witnesses.witness1,
                    image: true
                }
            })
            markImageChanged(ImageKey.WITNESS1_IMAGE)
        } catch (error) {
            console.error('Failed to save witness 1 image:', error)
        }
    }

    const handleWitness2NameChange = (name: string) => {
        updateWitnesses({
            witness2: {
                ...formData.witnesses.witness2,
                name
            }
        })
    }

    const handleWitness2ImageChange = async (image: string) => {
        try {
            await saveImage(ImageKey.WITNESS2_IMAGE, image, storageContext)
            setWitness2ImageData(image)
            updateWitnesses({
                witness2: {
                    ...formData.witnesses.witness2,
                    image: true
                }
            })
            markImageChanged(ImageKey.WITNESS2_IMAGE)
        } catch (error) {
            console.error('Failed to save witness 2 image:', error)
        }
    }

    const isEnabled = formData.witnesses.enabled !== false

    const handleToggle = () => {
        updateWitnesses({ enabled: !isEnabled })
    }

    if (loading) {
        return null
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.witnesses.title')}
            </Typography>

            <Box css={ToggleContainerStyle}>
                <FormControlLabel
                    css={ToggleLabelStyle}
                    control={
                        <Switch
                            css={SwitchStyle}
                            checked={isEnabled}
                            onChange={handleToggle}
                        />
                    }
                    label={t('questionnaire.witnesses.enableToggle')}
                />
            </Box>

            {isEnabled && (
                <Box css={FormContainerStyle}>
                    <WitnessInput
                        label={t('questionnaire.witnesses.witness1Label')}
                        name={formData.witnesses.witness1.name}
                        image={witness1ImageData}
                        onNameChange={handleWitness1NameChange}
                        onImageChange={handleWitness1ImageChange}
                    />
                    <WitnessInput
                        label={t('questionnaire.witnesses.witness2Label')}
                        name={formData.witnesses.witness2.name}
                        image={witness2ImageData}
                        onNameChange={handleWitness2NameChange}
                        onImageChange={handleWitness2ImageChange}
                    />
                </Box>
            )}
        </div>
    )
}
